import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEvent } from '@/hooks/use-events'
import {
  Button,
  Input,
  Label,
  Textarea,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Switch,
  Skeleton,
  useToast,
  Separator,
} from '@lead-magnifier/ui'
import { ArrowLeft, Save, Palette, FileText, Mail, Shield, Send } from 'lucide-react'
import { useAuth } from '@/lib/auth-context'
import { sendEmail } from '@/lib/edge-functions'

export const Route = createFileRoute('/_authenticated/events/$eventId/settings')({
  component: EventSettingsPage,
})

function EventSettingsPage() {
  const { eventId } = Route.useParams()
  const { data: event, isLoading } = useEvent(eventId)
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Form state
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [introText, setIntroText] = useState('')
  const [downloadUrl, setDownloadUrl] = useState('')
  const [ctaButtonText, setCtaButtonText] = useState('')

  // Branding
  const [primaryColor, setPrimaryColor] = useState('#3b82f6')
  const [backgroundColor, setBackgroundColor] = useState('#007E87')
  const [logoUrl, setLogoUrl] = useState('')
  const [headerImageUrl, setHeaderImageUrl] = useState('')

  // Form settings
  const [doubleOptIn, setDoubleOptIn] = useState(true)
  const [nameRequired, setNameRequired] = useState(false)
  const [submitButtonText, setSubmitButtonText] = useState('')

  // Privacy
  const [privacyText, setPrivacyText] = useState('')
  const [privacyUrl, setPrivacyUrl] = useState('')

  // Email templates
  const [doiEmailSubject, setDoiEmailSubject] = useState('')
  const [doiEmailBody, setDoiEmailBody] = useState('')
  const [welcomeEmailSubject, setWelcomeEmailSubject] = useState('')
  const [welcomeEmailBody, setWelcomeEmailBody] = useState('')

  // Load event data into form
  useEffect(() => {
    if (event) {
      setName(event.name || '')
      setSlug(event.slug || '')
      setStartDate(event.start_date?.split('T')[0] || '')
      setEndDate(event.end_date?.split('T')[0] || '')
      setIntroText(event.intro_text || '')
      setDownloadUrl(event.download_url || '')
      setCtaButtonText(event.cta_button_text || 'Jetzt herunterladen')
      setPrimaryColor(event.primary_color || '#3b82f6')
      setBackgroundColor(event.background_color || '#007E87')
      setLogoUrl(event.logo_url || '')
      setHeaderImageUrl(event.header_image_url || '')
      setDoubleOptIn(event.double_opt_in ?? true)
      setNameRequired(event.name_required ?? false)
      setSubmitButtonText(event.submit_button_text || 'Daten senden')
      setPrivacyText(event.privacy_text || '')
      setPrivacyUrl(event.privacy_url || '')
      setDoiEmailSubject(event.doi_email_subject || '')
      setDoiEmailBody(event.doi_email_body || '')
      setWelcomeEmailSubject(event.welcome_email_subject || '')
      setWelcomeEmailBody(event.welcome_email_body || '')
    }
  }, [event])

  const updateMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('events')
        .update({
          name,
          slug,
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate).toISOString(),
          intro_text: introText || null,
          download_url: downloadUrl,
          cta_button_text: ctaButtonText,
          primary_color: primaryColor,
          background_color: backgroundColor,
          logo_url: logoUrl || null,
          header_image_url: headerImageUrl || null,
          double_opt_in: doubleOptIn,
          name_required: nameRequired,
          submit_button_text: submitButtonText,
          privacy_text: privacyText,
          privacy_url: privacyUrl || null,
          doi_email_subject: doiEmailSubject,
          doi_email_body: doiEmailBody,
          welcome_email_subject: welcomeEmailSubject,
          welcome_email_body: welcomeEmailBody,
          updated_at: new Date().toISOString(),
        })
        .eq('id', eventId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] })
      queryClient.invalidateQueries({ queryKey: ['events'] })
      toast({
        title: 'Gespeichert',
        description: 'Die Einstellungen wurden erfolgreich gespeichert.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description: error instanceof Error ? error.message : 'Speichern fehlgeschlagen',
        variant: 'destructive',
      })
    },
  })

  // Send test email mutation
  const sendTestEmail = useMutation({
    mutationFn: async (type: 'doi' | 'welcome') => {
      if (!user?.email) throw new Error('Keine E-Mail-Adresse')

      const testData = {
        type: type === 'doi' ? 'test_doi' : 'test_welcome' as const,
        to: user.email,
        data: {
          name: user.user_metadata?.name || 'Test User',
          eventName: name || event?.name || 'Test Event',
          confirmationUrl: 'https://example.com/confirm/test-token',
          downloadUrl: downloadUrl || event?.download_url || 'https://example.com/download',
          subject: type === 'doi' ? doiEmailSubject : welcomeEmailSubject,
          body: type === 'doi' ? doiEmailBody : welcomeEmailBody,
        },
      }

      return sendEmail(testData)
    },
    onSuccess: (_, type) => {
      toast({
        title: 'Test-E-Mail gesendet',
        description: `${type === 'doi' ? 'DOI' : 'Willkommens'}-E-Mail wurde an ${user?.email} gesendet.`,
      })
    },
    onError: (error) => {
      toast({
        title: 'Fehler beim Senden',
        description: error instanceof Error ? error.message : 'E-Mail konnte nicht gesendet werden',
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Event nicht gefunden.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/events/${eventId}`} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Event-Einstellungen</h1>
            <p className="text-sm text-muted-foreground">{event.name}</p>
          </div>
        </div>
        <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {updateMutation.isPending ? 'Speichert...' : 'Speichern'}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-[600px]">
          <TabsTrigger value="general">
            <FileText className="h-4 w-4 mr-2 hidden sm:inline" />
            Allgemein
          </TabsTrigger>
          <TabsTrigger value="branding">
            <Palette className="h-4 w-4 mr-2 hidden sm:inline" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="emails">
            <Mail className="h-4 w-4 mr-2 hidden sm:inline" />
            E-Mails
          </TabsTrigger>
          <TabsTrigger value="privacy">
            <Shield className="h-4 w-4 mr-2 hidden sm:inline" />
            Datenschutz
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Allgemeine Informationen</CardTitle>
              <CardDescription>Grundlegende Event-Details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Event-Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">URL-Slug</Label>
                  <Input
                    id="slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  />
                  <p className="text-xs text-muted-foreground">
                    /form/.../{slug}
                  </p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Startdatum</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">Enddatum</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="introText">Intro-Text</Label>
                <Textarea
                  id="introText"
                  value={introText}
                  onChange={(e) => setIntroText(e.target.value)}
                  placeholder="Wird über dem Formular angezeigt..."
                  rows={3}
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="downloadUrl">Download-URL</Label>
                <Input
                  id="downloadUrl"
                  type="url"
                  value={downloadUrl}
                  onChange={(e) => setDownloadUrl(e.target.value)}
                  placeholder="https://example.com/whitepaper.pdf"
                />
                <p className="text-xs text-muted-foreground">
                  Link zur Datei, die Besucher nach Bestätigung erhalten
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ctaButtonText">Download-Button Text</Label>
                <Input
                  id="ctaButtonText"
                  value={ctaButtonText}
                  onChange={(e) => setCtaButtonText(e.target.value)}
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Double Opt-In</Label>
                    <p className="text-sm text-muted-foreground">
                      E-Mail-Bestätigung erforderlich (DSGVO-konform)
                    </p>
                  </div>
                  <Switch
                    checked={doubleOptIn}
                    onCheckedChange={setDoubleOptIn}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Name erforderlich</Label>
                    <p className="text-sm text-muted-foreground">
                      Besucher müssen ihren Namen angeben
                    </p>
                  </div>
                  <Switch
                    checked={nameRequired}
                    onCheckedChange={setNameRequired}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="submitButtonText">Absenden-Button Text</Label>
                <Input
                  id="submitButtonText"
                  value={submitButtonText}
                  onChange={(e) => setSubmitButtonText(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
              <CardDescription>Passe das Aussehen deines Formulars an</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="primaryColor">Primärfarbe</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primaryColor"
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Buttons und Akzente
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="backgroundColor">Hintergrundfarbe</Label>
                  <div className="flex gap-2">
                    <Input
                      id="backgroundColor"
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Hintergrund-Gradient
                  </p>
                </div>
              </div>

              {/* Color Preview */}
              <div
                className="h-24 rounded-lg flex items-center justify-center text-white font-medium"
                style={{
                  background: `linear-gradient(135deg, ${backgroundColor} 0%, ${primaryColor} 100%)`,
                }}
              >
                Vorschau
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="logoUrl">Logo-URL</Label>
                <Input
                  id="logoUrl"
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-muted-foreground">
                  Quadratisches Logo (min. 200x200px)
                </p>
              </div>

              {logoUrl && (
                <div className="flex items-center gap-4">
                  <img
                    src={logoUrl}
                    alt="Logo Preview"
                    className="w-16 h-16 rounded-lg object-contain bg-muted"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                  <span className="text-sm text-muted-foreground">Logo-Vorschau</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="headerImageUrl">Header-Bild URL</Label>
                <Input
                  id="headerImageUrl"
                  type="url"
                  value={headerImageUrl}
                  onChange={(e) => setHeaderImageUrl(e.target.value)}
                  placeholder="https://example.com/header.jpg"
                />
                <p className="text-xs text-muted-foreground">
                  Breites Banner-Bild oben im Formular
                </p>
              </div>

              {headerImageUrl && (
                <img
                  src={headerImageUrl}
                  alt="Header Preview"
                  className="w-full h-32 rounded-lg object-cover"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Emails Tab */}
        <TabsContent value="emails">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Bestätigungs-E-Mail (DOI)</CardTitle>
                <CardDescription>
                  Wird nach dem Absenden des Formulars gesendet
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="doiEmailSubject">Betreff</Label>
                  <Input
                    id="doiEmailSubject"
                    value={doiEmailSubject}
                    onChange={(e) => setDoiEmailSubject(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="doiEmailBody">Inhalt</Label>
                  <Textarea
                    id="doiEmailBody"
                    value={doiEmailBody}
                    onChange={(e) => setDoiEmailBody(e.target.value)}
                    rows={8}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Verfügbare Variablen: {"{{name}}"}, {"{{confirmationUrl}}"}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => sendTestEmail.mutate('doi')}
                  disabled={sendTestEmail.isPending}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sendTestEmail.isPending ? 'Sendet...' : 'Test-E-Mail senden'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Willkommens-E-Mail</CardTitle>
                <CardDescription>
                  Wird nach erfolgreicher Bestätigung gesendet
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="welcomeEmailSubject">Betreff</Label>
                  <Input
                    id="welcomeEmailSubject"
                    value={welcomeEmailSubject}
                    onChange={(e) => setWelcomeEmailSubject(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="welcomeEmailBody">Inhalt</Label>
                  <Textarea
                    id="welcomeEmailBody"
                    value={welcomeEmailBody}
                    onChange={(e) => setWelcomeEmailBody(e.target.value)}
                    rows={8}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Verfügbare Variablen: {"{{name}}"}, {"{{downloadUrl}}"}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => sendTestEmail.mutate('welcome')}
                  disabled={sendTestEmail.isPending}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sendTestEmail.isPending ? 'Sendet...' : 'Test-E-Mail senden'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Privacy Tab */}
        <TabsContent value="privacy">
          <Card>
            <CardHeader>
              <CardTitle>Datenschutz</CardTitle>
              <CardDescription>DSGVO-konforme Einstellungen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="privacyText">Consent-Text</Label>
                <Textarea
                  id="privacyText"
                  value={privacyText}
                  onChange={(e) => setPrivacyText(e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Text für die Datenschutz-Checkbox
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="privacyUrl">Datenschutzerklärung URL</Label>
                <Input
                  id="privacyUrl"
                  type="url"
                  value={privacyUrl}
                  onChange={(e) => setPrivacyUrl(e.target.value)}
                  placeholder="https://example.com/datenschutz"
                />
                <p className="text-xs text-muted-foreground">
                  Link zu deiner Datenschutzerklärung
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
